import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Sidenav from "examples/Sidenav";
import routes from "routes";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import { useMaterialUIController, setMiniSidenav, setOpenSide } from "context";
import { useApi } from "lib/api";
import { useMarcasEstandar } from "lib/marcasEstandar";
import AvisoMarcasEstandar from "lib/AvisoMarcasEstandar";
import { ocultaCostos } from "lib/seguridad/permisosPorModulo";
import { useMultiempresa } from "lib/multiempresa";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { documentoValido, formatDocumentoAuto, tipoDocumento } from "lib/documento";
import { parseExcelPreview, downloadTemplateXlsx } from "./excelPreview";
import LotesProductosImportados from "./LotesProductosImportados";
import {
  generarTablaAmortizacionFlat,
  formatearMoneda,
  formatearFecha,
  validarPrestamoRow,
  GARANTE_COLUMNAS,
  MATRICULA_COLUMNAS,
  MATRICULA_CUOTAS_DEFECTO,
} from "./amortizacionFlat";

const CLIENTES_PRESTAMOS_TEMPLATE_COLUMNS = [
  "No prest",
  "cedula",
  "Nombres",
  "Apellidos",
  "Direccion",
  "Telefono",
  "Provincia",
  "celular",
  "sexo",
  "estado civil",
  "dependientes",
  "capital",
  "tasa",
  "frecuencia",
  "plazo",
  "fecha de inicio",
  "balance pendiente",
  "atraso",
  "mora",
  // Chasis del vehículo que respalda el financiamiento. Va en la plantilla
  // descargada pero es opcional: con chasis la fila entra por "Guardar todos
  // los préstamos" (pareo automático con el producto); sin chasis se guarda
  // una a una con el modal, como siempre.
  "chasis",
  // Garante (fiador de la solicitud): nombre, documento, dirección y
  // teléfono. Opcionales como grupo y todo o nada por fila: con alguna celda
  // llena deben venir las cuatro, sin ninguna el préstamo entra sin garante.
  ...GARANTE_COLUMNAS,
];

/** Columnas que el archivo de préstamos puede traer o no. */
const PRESTAMOS_COLUMNAS_OPCIONALES = ["chasis", ...GARANTE_COLUMNAS];

/** Devuelve la fila con la cédula del cliente y la del garante en la máscara
 *  canónica del tipo derivado (cédula/RNC/pasaporte), para que el préstamo
 *  quede como uno digitado a mano. Celdas vacías se quedan vacías. Un
 *  documento con otro largo (no se valida en la importación) va tal cual:
 *  la máscara lo recortaría. */
function conDocumentosFormateados(row) {
  const out = { ...row };
  for (const nombres of [
    ["cedula", "Cedula", "Cédula"],
    ["cedula garante", "cédula garante", "cedula fiador"],
  ]) {
    const k = findRowKeyByNames(row, nombres);
    const valor = k ? String(row[k] ?? "").trim() : "";
    if (valor === "") continue;
    out[k] = documentoValido(valor, tipoDocumento(valor)) ? formatDocumentoAuto(valor) : valor;
  }
  return out;
}

/** Tamaño de los bloques del guardado masivo: requests cortos (no amarran
 *  un hilo del servidor) y progreso visible entre bloque y bloque. */
const LOTE_PRESTAMOS_CHUNK = 25;
/** Productos: sin límite de filas por archivo; se envían en bloques para que
 *  ningún request choque con el tope de cuerpo de nginx ni con el timeout. */
const LOTE_PRODUCTOS_CHUNK = 200;

const STEPS = [
  {
    key: "productos",
    label: "Productos",
    description:
      "Cargue un archivo Excel (.xlsx o .xls) con sus productos. La primera fila debe ser la fila de encabezados.",
    hint: "Puede descargar la plantilla para usar exactamente estas columnas: Chasis, marca, modelo, color, maquina, costo, precio min, precio.",
    templateColumns: ["Chasis", "marca", "modelo", "color", "maquina", "costo", "precio min", "precio"],
    templateFileName: "plantilla_productos.xlsx",
  },
  {
    key: "prestamos",
    label: "Préstamos",
    description:
      "Último paso: cargue el archivo de préstamos a importar. La primera fila son los encabezados.",
    hint:
      "Plantilla unificada clientes+préstamos: No prest, cedula, Nombres, Apellidos, Direccion, Telefono, Provincia, celular, sexo, estado civil, dependientes, capital, tasa, frecuencia, plazo, fecha de inicio, balance pendiente, atraso, mora, chasis, garante, cedula garante, direccion garante, telefono garante. " +
      'La columna "chasis" es opcional: las filas que la traigan se importan todas juntas con "Guardar todos los préstamos" (el producto se parea automáticamente por chasis); las filas sin chasis se guardan una a una pulsando la fila. ' +
      'Las columnas del garante también son opcionales y van todo o nada: si una fila trae alguna de las cuatro debe traer las cuatro (garante, cedula garante, direccion garante, telefono garante); vacías, el préstamo se importa sin garante.',
    templateColumns: CLIENTES_PRESTAMOS_TEMPLATE_COLUMNS,
    templateFileName: "plantilla_clientes_prestamos.xlsx",
  },
];

/** Campo del suplidor en las filas del preview. No sale de la plantilla: lo
 *  llena el selector al cargar el archivo y se edita por fila en la grilla. */
const SUPLIDOR_FIELD = "suplidor";
const SIN_SUPLIDOR_LABEL = "(Sin suplidor)";

const AMORT_TABLE_GRID_COLS =
  "minmax(36px, 0.4fr) minmax(0, 1.1fr) repeat(5, minmax(0, 1fr)) minmax(0, 0.75fr)";

const amortTableCellSx = {
  px: 1.5,
  py: 0.75,
  fontSize: "0.8125rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const amortTableRowSx = {
  display: "grid",
  gridTemplateColumns: AMORT_TABLE_GRID_COLS,
  width: "100%",
  boxSizing: "border-box",
  alignItems: "center",
};

function createEmptyStepState() {
  return {
    fileName: null,
    headers: [],
    rows: [],
    rowCount: 0,
    error: null,
    loading: false,
    status: "idle",
    saveSummary: null,
    validationSummary: null,
  };
}

function getRowValueByNames(row, names) {
  if (!row || typeof row !== "object") return "";
  const keys = Object.keys(row).filter((k) => k !== "id");
  for (const wanted of names) {
    const match = keys.find((k) => k.trim().toLowerCase() === wanted.toLowerCase());
    if (match) return String(row[match] ?? "").trim();
  }
  return "";
}

function normalizeHeader(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function sucursalCodigoFrom(sucursal) {
  const raw =
    typeof sucursal === "string" ? sucursal : sucursal?.label || sucursal?.descripcion || "";
  return String(raw).split("-")[0].trim();
}

/** Clave real de la fila cuyo encabezado coincide (sin mayúsculas/espacios). */
function findRowKeyByNames(row, names) {
  const keys = Object.keys(row).filter((k) => k !== "id");
  for (const wanted of names) {
    const match = keys.find((k) => k.trim().toLowerCase() === wanted.toLowerCase());
    if (match) return match;
  }
  return null;
}

/** Celdas de precio/precio min vacías (o en 0) se rellenan con el catálogo de
 *  precios por marca/modelo (Configuración → Catálogos). Lo que el archivo
 *  trae con valor, manda. El catálogo llega con marca/modelo en mayúsculas. */
function rellenarPreciosCatalogo(row, catalogo) {
  const marca = getRowValueByNames(row, ["marca"]).toUpperCase();
  const modelo = getRowValueByNames(row, ["modelo"]).toUpperCase();
  if (!marca || !modelo || !Array.isArray(catalogo)) return row;
  const cat = catalogo.find((c) => c.marca === marca && c.modelo === modelo);
  if (!cat) return row;
  const num = (v) => {
    const n = parseFloat(String(v ?? "").replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };
  const out = { ...row };
  const kPrecio = findRowKeyByNames(row, ["precio"]);
  const kMin = findRowKeyByNames(row, ["precio min", "precio minimo", "preciominimo", "mprecio"]);
  if (kPrecio && !(num(row[kPrecio]) > 0) && cat.precio > 0) out[kPrecio] = cat.precio;
  if (kMin && !(num(row[kMin]) > 0) && cat.mprecio > 0) out[kMin] = cat.mprecio;
  return out;
}

/** Compara los encabezados del archivo con los de la plantilla. `opcionales`
 *  son columnas que el archivo puede traer o no sin romper la validación
 *  (multi-empresa: "empresa"; productos: "maquina"). Se filtran de AMBOS
 *  lados: así una columna opcional puede ir en la plantilla descargada y los
 *  archivos de plantillas anteriores que no la traen siguen siendo válidos. */
function headersMatchTemplate(actualHeaders, templateHeaders, opcionales = []) {
  const opcionalesNorm = (opcionales || []).map(normalizeHeader);
  const efectivos = actualHeaders.filter((h) => !opcionalesNorm.includes(normalizeHeader(h)));
  const esperados = templateHeaders.filter((h) => !opcionalesNorm.includes(normalizeHeader(h)));
  if (efectivos.length !== esperados.length) return false;
  return esperados.every(
    (expectedHeader, index) => normalizeHeader(efectivos[index]) === normalizeHeader(expectedHeader)
  );
}

function ImportacionDatos() {
  const location = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [stepsData, setStepsData] = useState(() => [
    createEmptyStepState(),
    createEmptyStepState(),
  ]);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  // Diálogo "Lotes importados": consultar y corregir productos ya importados.
  const [lotesOpen, setLotesOpen] = useState(false);
  const [amortModal, setAmortModal] = useState({
    open: false,
    error: null,
    filas: [],
    resumen: null,
  });
  const [savingPrestamo, setSavingPrestamo] = useState(false);
  const [savingProductos, setSavingProductos] = useState(false);
  const [productosProgress, setProductosProgress] = useState(null);
  // Guardado masivo de préstamos (filas con chasis): progreso por bloques y
  // resumen final con las filas que fallaron u omitidas.
  const [savingLote, setSavingLote] = useState(false);
  const [loteProgress, setLoteProgress] = useState(null);
  const [loteSummary, setLoteSummary] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [sucursalOptions, setSucursalOptions] = useState([]);
  const [productoOptions, setProductoOptions] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [prestamoSeleccionadoId, setPrestamoSeleccionadoId] = useState(null);
  const [prestamoSeleccionadoRow, setPrestamoSeleccionadoRow] = useState(null);
  const [configImport, setConfigImport] = useState({ pideAno: false, pideCilindraje: false });
  // "Matrícula incluida": el cliente cobraba la matrícula dentro de las
  // primeras cuotas. Agrega las columnas matricula / cuotas matricula a la
  // plantilla de préstamos y las lee en el cálculo de cada fila.
  const [matriculaIncluida, setMatriculaIncluida] = useState(false);
  // "Cuota fija": la matrícula no sube las primeras cuotas; sale del capital
  // del vehículo y se compensa en las restantes (caso de un cliente).
  const [matriculaCuotaFija, setMatriculaCuotaFija] = useState(false);
  const opcionesPrestamo = useMemo(
    () => ({ matricula: matriculaIncluida, matriculaCuotaFija: matriculaIncluida && matriculaCuotaFija }),
    [matriculaIncluida, matriculaCuotaFija]
  );
  // Catálogo de precios por marca/modelo (rellena celdas vacías del preview).
  const [preciosCatalogo, setPreciosCatalogo] = useState([]);
  // Multi-empresa: la empresa dueña de los chasis del lote se elige arriba
  // (la columna "empresa" del archivo sigue valiendo por fila si viene).
  const multiempresa = useMultiempresa();
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState("");
  // Suplidor (opcional): maestro de CxP. El selector fija el suplidor con que
  // entran las filas del archivo y cada fila se puede cambiar en la grilla.
  const [suplidorOptions, setSuplidorOptions] = useState([]);
  const [suplidorSeleccionado, setSuplidorSeleccionado] = useState(null);
  const [detalleCapital, setDetalleCapital] = useState("");
  const [detalleInteres, setDetalleInteres] = useState("");
  const [detalleBalancePendiente, setDetalleBalancePendiente] = useState("");
  const fileInputRef = useRef(null);
  const {nombreNegocio,roleWeb} = controller
  // Rol con "ocultarcostos": la plantilla de productos trae el costo de cada
  // chasis, así que no puede dar de alta inventario desde aquí.
  const sinCostos = ocultaCostos(controller.roleDetalles);
  const { post } = useApi();


  // Columnas de la plantilla de productos según flags de varinicial (año / cilindraje).
  const metaSteps = useMemo(() => {
    const productosTemplateColumns = [
      "Chasis",
      "marca",
      "modelo",
      "color",
      // Número de máquina (motor). Va en la plantilla descargada —misma
      // posición que la del modal bulk de inventario— pero también es
      // opcional: los Excel de plantillas anteriores siguen siendo válidos.
      "maquina",
      ...(configImport.pideAno ? ["año"] : []),
      ...(configImport.pideCilindraje ? ["cilindraje"] : []),
      "costo",
      "precio min",
      "precio",
    ];
    // Multi-empresa: la empresa sale del selector, pero el archivo puede traer
    // una columna "empresa" (código 1, 2, ...) para mezclar empresas en un lote.
    // "cilindraje" va en la plantilla descargada cuando el tenant lo pide,
    // pero no es obligatorio: un archivo sin la columna (o con celdas vacías)
    // también es válido.
    const productosColumnasOpcionales = [
      "maquina",
      "cilindraje",
      ...(multiempresa.activo ? ["empresa"] : []),
    ];
    return STEPS.map((step) =>
      step.key === "productos"
        ? {
            ...step,
            templateColumns: productosTemplateColumns,
            columnasOpcionales: productosColumnasOpcionales,
            hint: `Puede descargar la plantilla para usar exactamente estas columnas: ${productosTemplateColumns.join(
              ", "
            )}. Las columnas "maquina" (número de motor) y "cilindraje" son opcionales: un archivo sin ellas también es válido.${
              multiempresa.activo
                ? ' La empresa dueña de estos chasis se elige en el selector "Empresa". Solo agregue una columna "empresa" al archivo si necesita mezclar varias empresas en el mismo archivo.'
                : ""
            } El suplidor es opcional y no va en el archivo: se elige arriba y se puede cambiar fila por fila en la columna "Suplidor" de la vista previa.`,
          }
        : matriculaIncluida
        ? {
            ...step,
            templateColumns: [...step.templateColumns, ...MATRICULA_COLUMNAS],
            columnasOpcionales: [...PRESTAMOS_COLUMNAS_OPCIONALES, ...MATRICULA_COLUMNAS],
            hint:
              step.hint +
              ` Matrícula incluida: "matricula" es el monto total de la matrícula (sin interés) que se cobra dentro de las primeras cuotas y "cuotas matricula" en cuántas cuotas se reparte desde la cuota 1 (vacía = ${MATRICULA_CUOTAS_DEFECTO}). El "capital" es solo el del vehículo y el "balance pendiente" incluye lo que falte de matrícula. Sin matrícula, deje ambas celdas vacías.${
                matriculaCuotaFija
                  ? " Cuota fija: vehículo + matrícula se reparten en partes iguales en todo el plazo; en las primeras cuotas la matrícula sale del capital del vehículo y se compensa en las restantes."
                  : ""
              }`,
          }
        : { ...step, columnasOpcionales: PRESTAMOS_COLUMNAS_OPCIONALES }
    );
  }, [configImport, multiempresa, matriculaIncluida, matriculaCuotaFija]);

  const current = stepsData[activeStep];
  // Switch "Estandarizar marcas y modelos": en el paso de productos se avisa
  // qué filas se guardarán con la escritura estándar.
  const estandar = useMarcasEstandar();
  const filasMarca = useMemo(
    () =>
      activeStep === 0
        ? (current?.rows || []).map((r) => ({
            marca: getRowValueByNames(r, ["marca"]),
            modelo: getRowValueByNames(r, ["modelo"]),
          }))
        : [],
    [activeStep, current]
  );
  const meta = metaSteps[activeStep];

  useEffect(() => {
    post("/api/inventariobulk/config")
      .then(({ ok, data }) => {
        const cfg = ok && data?.config ? data.config : {};
        setConfigImport({
          pideAno: !!cfg.pideAno,
          pideCilindraje: !!cfg.pideCilindraje,
        });
        setPreciosCatalogo(ok && Array.isArray(data?.precioscatalogo) ? data.precioscatalogo : []);
      })
      .catch(() => {
        setConfigImport({ pideAno: false, pideCilindraje: false });
        setPreciosCatalogo([]);
      });
  }, [post]);

  // Opciones del selector de empresa (mismo componente que el de sucursal
  // para que ambas cajas midan igual; el estado guarda solo el código).
  const empresaOptions = useMemo(
    () =>
      (multiempresa.empresas || []).map((e) => ({
        codigo: String(e.empresa),
        label: `${e.empresa} — ${e.nombre || `Empresa ${e.empresa}`}`,
      })),
    [multiempresa.empresas]
  );
  const empresaValue = empresaOptions.find((o) => o.codigo === empresaSeleccionada) ?? null;

  // Maestro de suplidores (misma lista del registro bulk). Ruta abierta a
  // cualquier usuario autenticado: no pide permiso propio. Si falla, la
  // columna queda sin opciones y la importación sigue sin suplidor.
  useEffect(() => {
    post("/api/inventariobulk/suppliers", {})
      .then(({ ok, data }) => {
        const list = ok && Array.isArray(data?.suppliers) ? data.suppliers : [];
        setSuplidorOptions(list.map((s) => ({ id: String(s.id), label: String(s.label || s.id) })));
      })
      .catch(() => setSuplidorOptions([]));
  }, [post]);

  // Con multi-empresa activo arranca en la primera empresa configurada.
  useEffect(() => {
    if (!multiempresa.activo) {
      setEmpresaSeleccionada("");
      return;
    }
    setEmpresaSeleccionada((prev) =>
      prev !== "" ? prev : String(multiempresa.empresas?.[0]?.empresa ?? "1")
    );
  }, [multiempresa]);

  useEffect(() => {
    function handleMiniSidenav() {
      setOpenSide(dispatch, true);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const gridColumns = useMemo(() => {
    if (!current.headers.length) return [];
    const delArchivo = current.headers.map((field) => ({
      field,
      headerName: field,
      flex: 1,
      minWidth: 110,
      sortable: false,
      align: "left",
      headerAlign: "left",
    }));
    // El suplidor no viene del archivo: se elige aquí, fila por fila.
    if (activeStep !== 0) return delArchivo;
    return [
      ...delArchivo,
      {
        field: SUPLIDOR_FIELD,
        headerName: "Suplidor",
        type: "singleSelect",
        editable: true,
        sortable: false,
        flex: 1.3,
        minWidth: 190,
        align: "left",
        headerAlign: "left",
        valueOptions: [
          { value: "", label: SIN_SUPLIDOR_LABEL },
          ...suplidorOptions.map((s) => ({ value: s.id, label: s.label })),
        ],
      },
    ];
  }, [activeStep, current.headers, suplidorOptions]);

  const productosPaso1 = useMemo(
    () =>
      stepsData[0].rows.map((row, index) => {
        const chasis = getRowValueByNames(row, ["chasis"]);
        const marca = getRowValueByNames(row, ["marca"]);
        const modelo = getRowValueByNames(row, ["modelo"]);
        const labelBase = [chasis, marca, modelo].filter(Boolean).join(" - ");
        return {
          id: row.id ?? index,
          label: labelBase || `Producto ${index + 1}`,
          row,
        };
      }),
    [stepsData]
  );

  const handlePickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const step = metaSteps[activeStep];
    downloadTemplateXlsx(step.templateFileName, step.templateColumns);
  }, [activeStep, metaSteps]);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      if (activeStep === 1) setLoteSummary(null);

      setStepsData((prev) => {
        const next = [...prev];
        next[activeStep] = {
          ...next[activeStep],
          loading: true,
          error: null,
        };
        return next;
      });

      try {
        // maxPreviewRows alto: aquí las filas parseadas SON las que se
        // importan (el default de 50 recortaba en silencio los lotes grandes).
        const result = await parseExcelPreview(file, { maxPreviewRows: 100000 });
        const headersValid = headersMatchTemplate(
          result.headers,
          meta.templateColumns,
          meta.columnasOpcionales
        );
        setStepsData((prev) => {
          const next = [...prev];
          const fileTag = `${result.fileName || "archivo"}-${Date.now()}`;
          const normalizedRows = (result.rows || []).map((row, index) => {
            const conId = { ...row, id: `${fileTag}-${index}` };
            if (activeStep !== 0) return conId;
            // Solo el paso de productos maneja precios y suplidor. Las filas
            // entran con el suplidor del selector y se editan una por una.
            return {
              ...rellenarPreciosCatalogo(conId, preciosCatalogo),
              [SUPLIDOR_FIELD]: suplidorSeleccionado?.id ?? "",
            };
          });
          const validationSummary =
            activeStep === 1
              ? normalizedRows.reduce(
                  (acc, row) => {
                    const validation = validarPrestamoRow(row, opcionesPrestamo);
                    if (validation.ok) acc.valid += 1;
                    else acc.invalid += 1;
                    return acc;
                  },
                  { valid: 0, invalid: 0 }
                )
              : null;
          next[activeStep] = {
            fileName: result.fileName,
            headers: result.headers,
            rows: normalizedRows,
            rowCount: result.rowCount,
            error: headersValid
              ? null
              : `Las columnas del archivo no coinciden con la plantilla de "${meta.label}". Deben ser exactamente: ${meta.templateColumns.join(", ")}.${
                  (meta.columnasOpcionales || []).length > 0
                    ? ` Opcionalmente puede agregar: ${meta.columnasOpcionales.join(", ")}.`
                    : ""
                }`,
            loading: false,
            status: headersValid ? "prevalidado" : "idle",
            saveSummary: null,
            validationSummary,
          };
          return next;
        });
        setPreviewVersion((v) => v + 1);
      } catch (err) {
        setStepsData((prev) => {
          const next = [...prev];
          next[activeStep] = {
            ...createEmptyStepState(),
            error: err?.message || "Error al leer el archivo.",
            loading: false,
          };
          return next;
        });
      }
    },
    [activeStep, meta, preciosCatalogo, suplidorSeleccionado, opcionesPrestamo]
  );

  /** Pone el suplidor del selector en todas las filas ya cargadas. */
  const handleAplicarSuplidorATodas = useCallback(() => {
    const id = suplidorSeleccionado?.id ?? "";
    setStepsData((prev) => {
      const next = [...prev];
      const step0 = next[0];
      if (!step0) return prev;
      next[0] = {
        ...step0,
        rows: (step0.rows || []).map((row) => ({ ...row, [SUPLIDOR_FIELD]: id })),
      };
      return next;
    });
    setPreviewVersion((v) => v + 1);
  }, [suplidorSeleccionado]);

  const handleClearFile = useCallback(() => {
    setStepsData((prev) => {
      const next = [...prev];
      next[activeStep] = createEmptyStepState();
      return next;
    });
    if (activeStep === 1) setLoteSummary(null);
  }, [activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep((s) => Math.max(0, s - 1));
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow) => {
      setStepsData((prev) => {
        const next = [...prev];
        const step = next[activeStep];
        if (!step) return prev;

        const updatedRows = (step.rows || []).map((row) => (row.id === newRow.id ? newRow : row));
        next[activeStep] = {
          ...step,
          rows: updatedRows,
        };
        return next;
      });
      return newRow;
    },
    [activeStep]
  );

  const handleFinish = useCallback(() => {
    setFinishConfirmOpen(true);
  }, []);

  const handleGuardarProductosLote = useCallback(async () => {
    const productosRows = stepsData[0]?.rows ?? [];
    if (productosRows.length === 0) return;
    if (sinCostos) {
      setStepsData((prev) => {
        const next = [...prev];
        next[0] = {
          ...next[0],
          error:
            "Su rol no puede agregar chasis al inventario porque no tiene acceso a los costos.",
        };
        return next;
      });
      return;
    }
    setSavingProductos(true);
    setStepsData((prev) => {
      const next = [...prev];
      next[0] = {
        ...next[0],
        error: null,
        status: "listo_para_guardar",
      };
      return next;
    });
    setProductosProgress({ done: 0, total: productosRows.length });

    // Índices globales (posición en la grilla) de lo guardado y de lo fallido;
    // el backend responde con índices relativos a cada bloque.
    const insertedIds = new Set();
    const errors = [];
    let corte = null;
    // Carga (lote) del archivo: el primer bloque la crea y los siguientes,
    // y los reintentos del mismo archivo, la continúan.
    let cargaId = stepsData[0]?.cargaId ?? null;
    try {
      for (let i = 0; i < productosRows.length; i += LOTE_PRODUCTOS_CHUNK) {
        const chunk = productosRows.slice(i, i + LOTE_PRODUCTOS_CHUNK);
        const { ok, data } = await post("/api/importacion/productos/lote", {
          productos: chunk,
          sucursal: sucursalCodigoFrom(sucursalSeleccionada),
          // Multi-empresa: empresa del lote; la columna "empresa" de una fila,
          // si viene, manda sobre esta.
          ...(multiempresa.activo ? { empresa: empresaSeleccionada } : {}),
          // Suplidor del lote: respaldo para las filas que queden sin suplidor
          // propio (cada fila lleva el suyo en el campo "suplidor").
          suplidor_id: suplidorSeleccionado?.id ?? "",
          allOrNothing: false,
          carga: cargaId,
          archivo: stepsData[0]?.fileName ?? "",
        });
        if (!ok || data?.ok === false) {
          throw new Error(data?.message || data?.Error || "No se pudo guardar el lote de productos.");
        }
        if (data?.data?.carga) cargaId = data.data.carga;
        (data?.data?.inserted ?? []).forEach((x) => insertedIds.add(i + x.index));
        (data?.data?.errors ?? []).forEach((err) =>
          errors.push({ ...err, index: Number.isFinite(err?.index) ? i + err.index : err?.index })
        );
        setProductosProgress({
          done: Math.min(i + chunk.length, productosRows.length),
          total: productosRows.length,
        });
      }
    } catch (e) {
      // Los bloques ya confirmados quedan guardados; las filas restantes
      // siguen en la grilla para reintentar.
      corte = e?.message || "No se pudo guardar el lote de productos.";
    }

    setStepsData((prev) => {
      const next = [...prev];
      const step0 = next[0];
      const remainingRows = (step0.rows || []).filter((_, index) => !insertedIds.has(index));
      next[0] = {
        ...step0,
        cargaId,
        rows: remainingRows,
        rowCount: remainingRows.length,
        status: insertedIds.size > 0 ? "guardado" : "prevalidado",
        saveSummary:
          insertedIds.size > 0 || errors.length > 0
            ? { inserted: insertedIds.size, failed: errors.length, errors }
            : null,
        error:
          corte && insertedIds.size > 0
            ? `${corte} Se guardaron ${insertedIds.size} productos antes del corte; las filas restantes siguen en la grilla.`
            : corte,
      };
      return next;
    });
    setProductosProgress(null);
    setSavingProductos(false);
  }, [stepsData, sucursalSeleccionada, sinCostos, multiempresa.activo, empresaSeleccionada, suplidorSeleccionado, post]);

  /** Guardado masivo del paso de préstamos: importa de una vez todas las
   *  filas válidas que traen chasis (el backend parea el producto por
   *  chasis). Envía bloques de LOTE_PRESTAMOS_CHUNK; las filas guardadas
   *  salen de la grilla y las fallidas/omitidas se quedan con su motivo. */
  const handleGuardarPrestamosLote = useCallback(async () => {
    const rows = stepsData[1]?.rows ?? [];
    if (rows.length === 0 || savingLote) return;

    const importables = [];
    let sinChasis = 0;
    let invalidas = 0;
    rows.forEach((row) => {
      const chasis = getRowValueByNames(row, ["chasis"]);
      if (!chasis) {
        sinChasis += 1;
        return;
      }
      const amort = generarTablaAmortizacionFlat(row, opcionesPrestamo);
      if (!amort.ok) {
        invalidas += 1;
        return;
      }
      // Cédula del cliente y del garante con su máscara canónica.
      const prestamo = conDocumentosFormateados(row);
      importables.push({
        rowId: row.id,
        etiqueta:
          [getRowValueByNames(row, ["cedula"]), chasis].filter(Boolean).join(" / ") || "(fila)",
        item: {
          prestamo,
          amortizacion: amort.filas,
          interesPagado: amort.resumen.interesPagado,
          capitalPagado: amort.resumen.capitalPagado,
        },
      });
    });

    if (importables.length === 0) {
      setLoteSummary({ inserted: 0, errors: [], sinChasis, invalidas });
      return;
    }

    setSavingLote(true);
    setLoteSummary(null);
    setLoteProgress({ done: 0, total: importables.length });

    const insertedIds = new Set();
    const errores = [];
    try {
      for (let i = 0; i < importables.length; i += LOTE_PRESTAMOS_CHUNK) {
        const chunk = importables.slice(i, i + LOTE_PRESTAMOS_CHUNK);
        const { ok, data } = await post("/api/importacion/prestamos/lote", {
          prestamos: chunk.map((c) => c.item),
          sucursal: sucursalCodigoFrom(sucursalSeleccionada),
        });
        if (!ok || data?.ok === false) {
          throw new Error(
            data?.message || data?.Error || "No se pudo guardar el bloque de préstamos."
          );
        }
        (data?.data?.inserted ?? []).forEach((ins) => {
          const c = chunk[ins.index];
          if (c) insertedIds.add(c.rowId);
        });
        (data?.data?.errors ?? []).forEach((err) => {
          const c = chunk[err.index];
          errores.push({
            etiqueta: c?.etiqueta || "(fila)",
            message: err?.message || "Error no especificado.",
          });
        });
        setLoteProgress({
          done: Math.min(i + chunk.length, importables.length),
          total: importables.length,
        });
      }
    } catch (e) {
      // Los bloques ya confirmados quedan guardados; se reporta el corte y
      // las filas restantes siguen en la grilla para reintentar.
      errores.push({
        etiqueta: "Lote interrumpido",
        message: e?.message || "No se pudo completar el guardado masivo.",
      });
    }

    setStepsData((prev) => {
      const next = [...prev];
      const paso = next[1];
      if (!paso) return prev;
      const restantes = (paso.rows || []).filter((r) => !insertedIds.has(r.id));
      const validationSummary = restantes.reduce(
        (acc, row) => {
          if (validarPrestamoRow(row, opcionesPrestamo).ok) acc.valid += 1;
          else acc.invalid += 1;
          return acc;
        },
        { valid: 0, invalid: 0 }
      );
      next[1] = {
        ...paso,
        rows: restantes,
        rowCount: restantes.length,
        status: insertedIds.size > 0 ? "guardado" : paso.status,
        validationSummary,
      };
      return next;
    });
    setLoteSummary({ inserted: insertedIds.size, errors: errores, sinChasis, invalidas });
    setLoteProgress(null);
    setSavingLote(false);
  }, [stepsData, savingLote, sucursalSeleccionada, post, opcionesPrestamo]);

  const handleConfirmFinish = useCallback(() => {
    setActiveStep(0);
    setStepsData([createEmptyStepState(), createEmptyStepState()]);
    setSaveError("");
    setAmortModal({ open: false, error: null, filas: [], resumen: null });
    setSavingPrestamo(false);
    setSavingLote(false);
    setLoteProgress(null);
    setLoteSummary(null);
    setProductoSeleccionado(null);
    setSucursalSeleccionada(sucursalOptions[0] ?? null);
    setSuplidorSeleccionado(null);
    setPrestamoSeleccionadoId(null);
    setPrestamoSeleccionadoRow(null);
    setFinishConfirmOpen(false);
  }, [sucursalOptions]);

  const handleGridRowClick = useCallback((params) => {
    if (activeStep !== 1 || current.error || savingLote) return;
    setPrestamoSeleccionadoId(params.row?.id ?? null);
    setPrestamoSeleccionadoRow(params.row ?? null);
    setDetalleCapital(getRowValueByNames(params.row, ["capital", "Capital"]));
    setDetalleInteres(getRowValueByNames(params.row, ["tasa", "Tasa", "interes", "Interes"]));
    setDetalleBalancePendiente(
      getRowValueByNames(params.row, ["balance pendiente", "balance_pendiente", "balancependiente"])
    );
    const result = generarTablaAmortizacionFlat(params.row, opcionesPrestamo);
    if (!result.ok) {
      setAmortModal({
        open: true,
        error: result.error,
        filas: [],
        resumen: null,
      });
      return;
    }
    setAmortModal({
      open: true,
      error: null,
      filas: result.filas,
      resumen: result.resumen,
    });
  }, [activeStep, current.error, savingLote, opcionesPrestamo]);

  const handleCloseAmortModal = useCallback(() => {
    setAmortModal((prev) => ({ ...prev, open: false }));
    setSaveError("");
    setProductoSeleccionado(null);
    setPrestamoSeleccionadoId(null);
    setPrestamoSeleccionadoRow(null);
    setDetalleCapital("");
    setDetalleInteres("");
    setDetalleBalancePendiente("");
  }, []);

  useEffect(() => {
    if (!amortModal.open || prestamoSeleccionadoRow == null) return;
    const rowEditado = {
      ...prestamoSeleccionadoRow,
      capital: detalleCapital,
      Capital: detalleCapital,
      tasa: detalleInteres,
      Tasa: detalleInteres,
      "balance pendiente": detalleBalancePendiente,
      balance_pendiente: detalleBalancePendiente,
      balancependiente: detalleBalancePendiente,
    };
    const result = generarTablaAmortizacionFlat(rowEditado, opcionesPrestamo);
    if (!result.ok) {
      setAmortModal((prev) => ({
        ...prev,
        error: result.error,
        filas: [],
        resumen: null,
      }));
      return;
    }
    setPrestamoSeleccionadoRow(rowEditado);
    setAmortModal((prev) => ({
      ...prev,
      error: null,
      filas: result.filas,
      resumen: result.resumen,
    }));
  }, [amortModal.open, prestamoSeleccionadoId, detalleCapital, detalleInteres, detalleBalancePendiente, opcionesPrestamo]);

  useEffect(() => {
    setLoadingSucursales(true);
    post("/api/recuperasucursales", { sucursal: " " })
      .then(({ data }) => {
        const list = Array.isArray(data?.sucursales) ? data.sucursales : [];
        setSucursalOptions(list);
        if (list.length > 0) {
          setSucursalSeleccionada((prev) => prev ?? list[0]);
        }
      })
      .catch(() => setSucursalOptions([]))
      .finally(() => setLoadingSucursales(false));
  }, [post]);

  useEffect(() => {
    if (!amortModal.open) return;
    setLoadingProductos(true);

    post("/api/importacion/productos/disponibles", {})
      .then(({ data }) => {
        const list = Array.isArray(data?.data?.productos) ? data.data.productos : [];
        
        if (list.length > 0) {
          
          setProductoOptions(list);
          return;
        }
        setProductoOptions(productosPaso1);
      })
      .catch(() => setProductoOptions(productosPaso1))
      .finally(() => setLoadingProductos(false));
  }, [amortModal.open, productosPaso1]);

  const handleGuardarPrestamo = useCallback(async () => {
    if (productoSeleccionado == null || prestamoSeleccionadoId == null || prestamoSeleccionadoRow == null) return;

    setSaveError("");
    setSavingPrestamo(true);

    const sucursalCodigo = sucursalCodigoFrom(sucursalSeleccionada);

    const body = {
      // Misma máscara de documentos que el guardado masivo (antes el modal
      // mandaba la cédula tal cual venía en el Excel).
      prestamo: conDocumentosFormateados(prestamoSeleccionadoRow),
      producto: productoSeleccionado.row,
      sucursal: sucursalCodigo,
      amortizacion: amortModal.filas,
      interesPagado: amortModal.resumen.interesPagado,
      capitalPagado: amortModal.resumen.capitalPagado,
      mora: prestamoSeleccionadoRow.mora,


    };

    try {
      const { ok, data } = await post("/api/importacion/prestamos/guardar", body);
      if (!ok) {
        throw new Error(data?.message || data?.Error || "No se pudo importar el préstamo.");
      }

      setStepsData((prev) => {
        const next = [...prev];

        const productos = next[0] ?? createEmptyStepState();
        const prestamos = next[1] ?? createEmptyStepState();

        const productosRows = productos.rows.filter((r) => r.id !== productoSeleccionado.id);
        const prestamosRows = prestamos.rows.filter((r) => r.id !== prestamoSeleccionadoId);

        next[0] = {
          ...productos,
          rows: productosRows,
          rowCount: Math.max(0, (productos.rowCount ?? productos.rows.length) - 1),
        };

        next[1] = {
          ...prestamos,
          rows: prestamosRows,
          rowCount: Math.max(0, (prestamos.rowCount ?? prestamos.rows.length) - 1),
          status: "guardado",
        };

        return next;
      });

      handleCloseAmortModal();
    } catch (e) {
      setSaveError(e?.message || "No se pudo guardar el préstamo.");
    } finally {
      setSavingPrestamo(false);
    }

  }, [
    productoSeleccionado,
    sucursalSeleccionada,
    prestamoSeleccionadoId,
    prestamoSeleccionadoRow,
    amortModal.filas,
    handleCloseAmortModal,
  ]);

  const isPrestamosStep = activeStep === 1;
  const productosState = stepsData[0];

  return (
    <Box component="main" sx={{ minHeight: '100%', bgcolor: 'grey.50', py: 3, px: { xs: 2, sm: 3 } }}>
      <MDBox py={0}>
      <Box component="main" sx={{ px: 0, pb: 0 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: "none" }}
          aria-label={`Seleccionar archivo Excel para ${meta.label}`}
          onChange={handleFileChange}
        />

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "background.paper" }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" component="h1" gutterBottom>
              Paso {activeStep + 1} de {STEPS.length}: {meta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {meta.description}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {meta.hint}
            </Alert>
            {activeStep === 0 && sinCostos && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Su rol no tiene acceso a los costos, por lo que no puede agregar chasis al
                inventario.
              </Alert>
            )}
            {current.status !== "idle" && (
              <Alert severity={current.status === "guardado" ? "success" : "info"} sx={{ mb: 2 }}>
                Estado actual: <strong>{current.status}</strong>
                {current.validationSummary && (
                  <>
                    {" "}
                    | Válidos: {current.validationSummary.valid} | Inválidos:{" "}
                    {current.validationSummary.invalid}
                  </>
                )}
              </Alert>
            )}
            {activeStep === 1 && loteSummary && (
              <Alert
                severity={
                  loteSummary.errors.length > 0
                    ? "warning"
                    : loteSummary.inserted > 0
                      ? "success"
                      : "info"
                }
                sx={{ mb: 2 }}
              >
                Préstamos importados: {loteSummary.inserted}. Errores: {loteSummary.errors.length}.
                {(loteSummary.sinChasis > 0 || loteSummary.invalidas > 0) && (
                  <>
                    {" "}
                    Omitidas: {loteSummary.sinChasis} sin chasis
                    {loteSummary.invalidas > 0
                      ? ` y ${loteSummary.invalidas} con datos inválidos`
                      : ""}
                    ; siguen en la lista para corregirlas o guardarlas una a una.
                  </>
                )}
                {loteSummary.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {loteSummary.errors.map((err, idx) => (
                      <Typography
                        key={`lote-error-${idx}`}
                        variant="body2"
                        sx={{ display: "block" }}
                      >
                        {err.etiqueta}: {err.message}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            {activeStep === 0 && current.saveSummary && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Productos guardados: {current.saveSummary.inserted}. Errores: {current.saveSummary.failed}.
                {Array.isArray(current.saveSummary.errors) && current.saveSummary.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {current.saveSummary.errors.map((err, idx) => {
                      const fila = Number.isFinite(err?.index) ? err.index + 1 : "?";
                      const campo = err?.field ? ` | Campo: ${err.field}` : "";
                      const mensaje = err?.message || "Error no especificado.";
                      return (
                        <Typography key={`prod-error-${idx}`} variant="body2" sx={{ display: "block" }}>
                          Fila {fila}
                          {campo}: {mensaje}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Alert>
            )}
          </Grid>

          {/* La sucursal aplica a ambos pasos: destino de los productos y
              sucursal de los préstamos del guardado masivo. */}
          <Grid item xs={12} md={5} lg={4}>
            <Autocomplete
              value={sucursalSeleccionada}
              onChange={(_, value) => setSucursalSeleccionada(value)}
              options={sucursalOptions}
              loading={loadingSucursales}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.label || option?.descripcion || ""
              }
              isOptionEqualToValue={(option, value) =>
                (option?.id ?? option?.label ?? option) === (value?.id ?? value?.label ?? value)
              }
              noOptionsText="No hay sucursales disponibles"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sucursal de destino"
                  placeholder={
                    activeStep === 0
                      ? "Seleccione la sucursal para los productos"
                      : "Seleccione la sucursal para los préstamos"
                  }
                />
              )}
            />
          </Grid>

          {activeStep === 0 && multiempresa.activo && (
            <Grid item xs={12} md={5} lg={4}>
              <Autocomplete
                value={empresaValue}
                onChange={(_, value) => setEmpresaSeleccionada(value?.codigo ?? "")}
                options={empresaOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.codigo === value?.codigo}
                noOptionsText="No hay empresas disponibles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    placeholder="Seleccione la empresa dueña de los chasis"
                  />
                )}
              />
            </Grid>
          )}

          {activeStep === 0 && (
            <Grid item xs={12} md={5} lg={4}>
              <Autocomplete
                value={suplidorSeleccionado}
                onChange={(_, value) => setSuplidorSeleccionado(value)}
                options={suplidorOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                noOptionsText="No hay suplidores disponibles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Suplidor (opcional)"
                    placeholder="Suplidor con el que entran las filas"
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {isPrestamosStep && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={matriculaIncluida}
                      onChange={(e) => setMatriculaIncluida(e.target.checked)}
                    />
                  }
                  label="Matrícula incluida"
                  // Las filas ya cargadas se validaron con la otra plantilla:
                  // para cambiarlo hay que quitar el archivo.
                  disabled={!!current.fileName || current.loading || savingLote}
                  title={
                    current.fileName
                      ? "Quite el archivo para cambiar esta opción"
                      : "La matrícula se cobró dentro de las primeras cuotas: agrega las columnas matricula y cuotas matricula"
                  }
                />
              )}
              {isPrestamosStep && matriculaIncluida && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={matriculaCuotaFija}
                      onChange={(e) => setMatriculaCuotaFija(e.target.checked)}
                    />
                  }
                  label="Cuota fija"
                  disabled={!!current.fileName || current.loading || savingLote}
                  title={
                    current.fileName
                      ? "Quite el archivo para cambiar esta opción"
                      : "La matrícula no sube las primeras cuotas: sale del capital del vehículo y se compensa en las cuotas restantes"
                  }
                />
              )}
              {activeStep === 0 && (
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() => setLotesOpen(true)}
                  aria-label="Ver y editar lotes de productos importados"
                >
                  Lotes importados
                </MDButton>
              )}
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleDownloadTemplate}
                startIcon={<DownloadOutlinedIcon />}
                aria-label={`Descargar plantilla Excel para ${meta.label}`}
              >
                Descargar plantilla
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handlePickFile}
                disabled={current.loading}
                aria-label={`Elegir archivo Excel para ${meta.label}`}
              >
                {current.fileName ? "Cambiar archivo" : "Elegir archivo Excel"}
              </MDButton>
              {current.fileName && (
                <MDButton variant="outlined" color="dark" onClick={handleClearFile} disabled={current.loading}>
                  Quitar archivo
                </MDButton>
              )}
              {activeStep === 0 && current.rows.length > 0 && (
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={handleAplicarSuplidorATodas}
                  disabled={current.loading}
                >
                  {suplidorSeleccionado
                    ? "Aplicar suplidor a todas las filas"
                    : "Quitar suplidor de todas las filas"}
                </MDButton>
              )}
              {current.fileName && (
                <Chip label={current.fileName} variant="outlined" size="small" />
              )}
              {current.rowCount > 0 && (
                <Chip
                  label={`${current.rowCount} fila${current.rowCount === 1 ? "" : "s"} de datos`}
                  color="primary"
                  size="small"
                />
              )}
              {current.loading && <CircularProgress size={22} aria-label="Leyendo archivo" />}
            </Stack>
            {current.loading && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress />
              </Box>
            )}
            {current.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {current.error}
              </Alert>
            )}
            {isPrestamosStep && current.rows.length > 0 && !current.loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Las filas con chasis se importan juntas con «Guardar todos los préstamos». Pulse
                una fila para revisar su tabla de amortización (método flat) o guardarla
                individualmente.
              </Typography>
            )}
          </Grid>

          {filasMarca.length > 0 && (
            <Grid item xs={12}>
              <AvisoMarcasEstandar filas={filasMarca} estandar={estandar} sx={{ mb: 0 }} />
            </Grid>
          )}
          <Grid item xs={12}>
            <MDBox
              sx={{
                height: 380,
                width: "100%",
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
              }}
            >
              {gridColumns.length > 0 ? (
                <DataGrid
                  key={`${activeStep}-${previewVersion}`}
                  rows={current.rows}
                  columns={gridColumns.map((col) => ({ ...col, editable: true }))}
                  onRowClick={isPrestamosStep ? handleGridRowClick : undefined}
                  processRowUpdate={handleProcessRowUpdate}
                  onProcessRowUpdateError={(error) => {
                    setStepsData((prev) => {
                      const next = [...prev];
                      next[activeStep] = {
                        ...next[activeStep],
                        error: error?.message || "No se pudo actualizar la fila editada.",
                      };
                      return next;
                    });
                  }}
                  disableRowSelectionOnClick
                  hideFooter
                  density="compact"
                  disableVirtualization
                />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: "100%", px: 2 }}
                  spacing={1}
                >
                  <Typography color="text.secondary" variant="body2" align="center">
                    {current.loading
                      ? "Leyendo archivo…"
                      : "La vista previa de las primeras filas aparecerá aquí después de elegir un archivo."}
                  </Typography>
                </Stack>
              )}
            </MDBox>
            {current.rowCount > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {current.rowCount} filas cargadas; todas se importan al guardar.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <MDButton variant="outlined" color="dark" onClick={handleBack} disabled={activeStep === 0}>
                Anterior
              </MDButton>
              {activeStep === 0 && (
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleGuardarProductosLote}
                  disabled={
                    savingProductos ||
                    sinCostos ||
                    current.rows.length === 0 ||
                    Boolean(current.error) ||
                    sucursalSeleccionada == null ||
                    (multiempresa.activo && empresaSeleccionada === "")
                  }
                >
                  {savingProductos && productosProgress
                    ? `Guardando ${productosProgress.done}/${productosProgress.total}...`
                    : "Guardar productos"}
                </MDButton>
              )}
              {isPrestamosStep && (
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleGuardarPrestamosLote}
                  disabled={
                    savingLote ||
                    savingPrestamo ||
                    current.rows.length === 0 ||
                    Boolean(current.error) ||
                    sucursalSeleccionada == null
                  }
                >
                  {savingLote && loteProgress
                    ? `Guardando ${loteProgress.done}/${loteProgress.total}...`
                    : "Guardar todos los préstamos"}
                </MDButton>
              )}
              {activeStep < STEPS.length - 1 ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleNext}
                >
                  Siguiente
                </MDButton>
              ) : (
                <MDButton variant="gradient" color="error" onClick={handleFinish}>
                  Finalizar importación
                </MDButton>
              )}
            </Stack>
            {savingProductos && productosProgress && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    productosProgress.total > 0
                      ? (productosProgress.done / productosProgress.total) * 100
                      : 0
                  }
                />
              </Box>
            )}
            {savingLote && loteProgress && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    loteProgress.total > 0 ? (loteProgress.done / loteProgress.total) * 100 : 0
                  }
                />
              </Box>
            )}
          </Grid>
        </Grid>

        <Dialog
          open={amortModal.open}
          onClose={handleCloseAmortModal}
          maxWidth="xl"
          fullWidth
          scroll="paper"
          aria-labelledby="amortizacion-dialog-title"
        >
          <DialogTitle id="amortizacion-dialog-title" sx={{ pr: 6 }}>
            Detalles
            <IconButton
              aria-label="Cerrar"
              onClick={handleCloseAmortModal}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ width: "100%", boxSizing: "border-box", minWidth: 0 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Capital"
                  value={detalleCapital}
                  onChange={(e) => setDetalleCapital(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Interés (tasa)"
                  value={detalleInteres}
                  onChange={(e) => setDetalleInteres(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Balance pendiente"
                  value={detalleBalancePendiente}
                  onChange={(e) => setDetalleBalancePendiente(e.target.value)}
                />
              </Grid>
            </Grid>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                value={productoSeleccionado}
                onChange={(_, value) => setProductoSeleccionado(value)}
                options={productoOptions}
                loading={loadingProductos}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No hay productos disponibles (estado distinto de V)"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producto disponible"
                    placeholder="Seleccione un producto"
                  />
                )}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                value={sucursalSeleccionada}
                onChange={(_, value) => setSucursalSeleccionada(value)}
                options={sucursalOptions}
                loading={loadingSucursales}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.label || option?.descripcion || ""
                }
                isOptionEqualToValue={(option, value) =>
                  (option?.id ?? option?.label ?? option) === (value?.id ?? value?.label ?? value)
                }
                noOptionsText="No hay sucursales disponibles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sucursal"
                    placeholder="Seleccione una sucursal"
                  />
                )}
              />
            </Box>
            {amortModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {amortModal.error}
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            {amortModal.resumen && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                </Typography>
                {(amortModal.resumen.capitalPagadoImporte > 0 ||
                  amortModal.resumen.interesPagadoImporte > 0) && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Importes importados (capital pagado:{" "}
                    <strong>{formatearMoneda(amortModal.resumen.capitalPagadoImporte)}</strong>, interés
                    pagado: <strong>{formatearMoneda(amortModal.resumen.interesPagadoImporte)}</strong>)
                    repartidos en partes iguales entre las {amortModal.resumen.plazo} cuotas en las columnas
                    «Capital pagado» e «Interés pagado».
                  </Typography>
                )}
                {amortModal.resumen.matricula > 0 && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Matrícula de <strong>{formatearMoneda(amortModal.resumen.matricula)}</strong> incluida
                    en el capital de las primeras {amortModal.resumen.cuotasMatricula} cuota
                    {amortModal.resumen.cuotasMatricula === 1 ? "" : "s"} (sin interés
                    {opcionesPrestamo.matriculaCuotaFija ? "; cuota fija, compensada en las cuotas restantes" : ""}). Pagada:{" "}
                    <strong>{formatearMoneda(amortModal.resumen.matriculaPagada)}</strong>.
                  </Typography>
                )}
              </Box>
            )}
            {amortModal.filas.length > 0 && (
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  {amortModal.filas.length} cuota{amortModal.filas.length === 1 ? "" : "s"}
                  {amortModal.resumen?.plazo != null && amortModal.filas.length !== amortModal.resumen.plazo
                    ? ` (plazo: ${amortModal.resumen.plazo})`
                    : ""}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxHeight: 420,
                    overflow: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      ...amortTableRowSx,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderBottom: 1,
                      borderColor: "divider",
                      fontWeight: 600,
                    }}
                  >
                    <Box sx={amortTableCellSx}>#</Box>
                    <Box sx={amortTableCellSx}>Fecha</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Cuota</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Capital</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Interés</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Capital pagado</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Interés pagado</Box>
                    <Box sx={amortTableCellSx}>Estado</Box>
                  </Box>
                  {amortModal.filas.map((f) => (
                    <Box
                      key={f.periodo}
                      sx={{
                        ...amortTableRowSx,
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Box sx={amortTableCellSx}>{f.periodo}</Box>
                      <Box sx={amortTableCellSx}>{formatearFecha(f.fecha)}</Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.cuota)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.capital)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.interes)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.capitalPagado)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.interesPagado)}
                      </Box>
                      <Box sx={amortTableCellSx}>
                        {f.estado === "pagada"
                          ? "Pagada"
                          : f.estado === "parcial"
                            ? "Parcial"
                            : "Pendiente"}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleGuardarPrestamo}
              disabled={
                savingPrestamo ||
                productoSeleccionado == null ||
                sucursalSeleccionada == null ||
                prestamoSeleccionadoId == null
              }
            >
              {savingPrestamo ? "Guardando..." : "Guardar préstamo"}
            </MDButton>
            <MDButton variant="outlined" color="dark" onClick={handleCloseAmortModal}>
              Cerrar
            </MDButton>
          </DialogActions>
        </Dialog>

        <LotesProductosImportados
          open={lotesOpen}
          onClose={() => setLotesOpen(false)}
          sucursalOptions={sucursalOptions}
          suplidorOptions={suplidorOptions}
        />

        <Dialog
          open={finishConfirmOpen}
          onClose={() => setFinishConfirmOpen(false)}
          aria-labelledby="confirmar-finalizar-importacion-title"
        >
          <DialogTitle id="confirmar-finalizar-importacion-title">
            ¿Desea finalizar la importación?
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary">
              Al finalizar se limpiarán los datos cargados y el proceso iniciará desde el paso 1.
            </Typography>
          </DialogContent>
          <DialogActions>
            <MDButton variant="outlined" color="dark" onClick={() => setFinishConfirmOpen(false)}>
              Cancelar
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleConfirmFinish}>
              Finalizar importación
            </MDButton>
          </DialogActions>
        </Dialog>
      </Box>
      </MDBox>
    </Box>
  );
}

export default ImportacionDatos;
